import { AiHttpService, ChatResponseModel } from './aiHttpService';

interface ChatMessage {
  text: string;
  isUser: boolean;
  citations?: string;
}

interface ChatHistoryItem {
  message: string;
  isUser: boolean;
  timestamp: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

interface ChatResponse {
  response: string;
  citations: string;
  chatId?: string;
  agentName?: string;
  agentType?: string;
}

interface ChatRequest {
  Message: string;
  ChatId?: string;
}

export class ChatAboutCandidatesService extends AiHttpService {
  private currentChatId: string | null = null;
  private chatHistory: ChatMessage[] = [];

  constructor() {
    super(); // No endpoint parameter needed anymore
  }

  // Uses inherited getAccessToken() from BaseService

  // Start a new chat session (clear current state)
  startNewChat(): void {
    this.currentChatId = null;
    this.chatHistory = [];
  }

  // Get current chat ID
  getCurrentChatId(): string | null {
    return this.currentChatId;
  }

  // Set chat ID for continuing existing conversation
  setChatId(chatId: string): void {
    this.currentChatId = chatId;
  }

  // Clear current chat session
  clearChat(): void {
    this.currentChatId = null;
    this.chatHistory = [];
  }

  // Method to get current chat history
  getChatHistory(): ChatMessage[] {
    return [...this.chatHistory];
  }

  // Method to load chat history from backend
  async loadChatHistory(chatId: string): Promise<ChatMessage[]> {
    try {
      console.log("[ChatAboutCandidatesService] Loading chat history for chatId:", chatId);

      const response = await this.get<ChatHistoryItem[]>(`/chat-history/${chatId}`);

      if (response && Array.isArray(response)) {
        // Clear current history and load from backend
        this.chatHistory = response.map(item => ({
          text: item.message,
          isUser: item.isUser
        }));

        // Set the current chat ID
        this.currentChatId = chatId;

        console.log("[ChatAboutCandidatesService] Loaded chat history:", this.chatHistory.length, "messages");
        return [...this.chatHistory];
      }

      return [];
    } catch (error) {
      console.error("[ChatAboutCandidatesService] Error loading chat history:", error);
      return [];
    }
  }

  async sendChatMessage(message: string, chatId?: string): Promise<ChatResponse> {
    const activeChatId = chatId || this.currentChatId || undefined;

    // Add user message to local history for UI display
    this.chatHistory.push({ text: message, isUser: true });

    const request: ChatRequest = {
      Message: message,
      ChatId: activeChatId // Let backend generate if not provided
    };

    const response = await this.post<ChatRequest, ChatResponse>(
      request,
      '/chat-with-ai-about-candidates'
    );

    // Store the ChatId from the response if this is a new conversation
    if (response.chatId && !this.currentChatId && !chatId) {
      this.currentChatId = response.chatId;
    }

    // Add AI response to local history for UI display
    this.chatHistory.push({
      text: response.response,
      isUser: false,
      citations: response.citations,
    });

    return response;
  }

  async* streamChatMessage(
    message: string,
    chatId?: string
  ): AsyncGenerator<ChatResponse, void, unknown> {
    try {
      const activeChatId = chatId || this.currentChatId || undefined;

      // Add user message to local history for UI display
      this.chatHistory.push({ text: message, isUser: true });

      console.log(
        "[ChatAboutCandidatesService] Sending streaming chat message to:",
        this.buildUrl("stream-chat-with-ai-about-candidates")
      );

      const request: ChatRequest = {
        Message: message,
        ChatId: activeChatId,
      };

      const url = this.buildUrl("/stream-chat-with-ai-about-candidates");
      const token = await this.getAccessToken();

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Response body is not readable");
      }

      let buffer = "";
      let fullResponse = "";
      let finalCitations = "";
      let receivedChatId: string | undefined;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim()) {
            try {
              const chunk: ChatResponse = JSON.parse(line);

              if (chunk.chatId) {
                receivedChatId = chunk.chatId;
                // Store the ChatId from the first response if this is a new conversation
                if (!this.currentChatId && !chatId) {
                  this.currentChatId = chunk.chatId;
                }
              }

              if (chunk.response) {
                fullResponse += chunk.response;
              }

              if (chunk.citations) {
                finalCitations = chunk.citations;
              }

              yield chunk;
            } catch (e) {
              console.error("[ChatAboutCandidatesService] Error parsing chunk:", e);
            }
          }
        }
      }

      // Add final AI response to local history
      this.chatHistory.push({
        text: fullResponse,
        isUser: false,
        citations: finalCitations,
      });

      console.log(
        "[ChatAboutCandidatesService] Streaming complete. ChatId:",
        receivedChatId || this.currentChatId
      );
    } catch (error) {
      console.error("[ChatAboutCandidatesService] Streaming error:", error);
      throw error;
    }
  }

  async generateOrgChartFromFile(file: File, chatId?: string): Promise<ChatResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const activeChatId = chatId || this.currentChatId;
    if (activeChatId) {
      formData.append('ChatId', activeChatId);
    }
    // If no ChatId, let backend generate one

    // Add user action to local history for UI display
    this.chatHistory.push({ text: `Uploaded file: ${file.name}`, isUser: true });

    const response = await this.postFormData<FormData, ChatResponse>(
      formData,
      '/generate-org-chart-from-file'
    );

    // Store the ChatId from the response if this is a new conversation
    if (response.chatId && !this.currentChatId && !chatId) {
      this.currentChatId = response.chatId;
    }

    // Add AI response to local history for UI display
    this.chatHistory.push({
      text: response.response,
      isUser: false,
      citations: response.citations,
    });

    return response;
  }

  async* streamGenerateOrgChartFromFile(
    file: File,
    chatId?: string
  ): AsyncGenerator<ChatResponseModel, void, unknown> {
    try {
      const activeChatId = chatId || this.currentChatId || undefined;

      // Add user action to local history for UI display
      this.chatHistory.push({ text: `Uploaded file: ${file.name}`, isUser: true });

      console.log(
        "[ChatAboutCandidatesService] Streaming org chart generation from file:",
        file.name
      );

      let fullResponse = "";
      let finalCitations = "";
      let receivedChatId: string | undefined;

      for await (const chunk of super.streamGenerateOrgChartFromFile(file, activeChatId)) {
        if (chunk.chatId) {
          receivedChatId = chunk.chatId;
          // Store the ChatId from the first response if this is a new conversation
          if (!this.currentChatId && !chatId) {
            this.currentChatId = chunk.chatId;
          }
        }

        if (chunk.response) {
          fullResponse += chunk.response;
        }

        if (chunk.citations) {
          finalCitations = chunk.citations;
        }

        yield chunk;
      }

      // Add final AI response to local history
      this.chatHistory.push({
        text: fullResponse,
        isUser: false,
        citations: finalCitations,
      });

      console.log(
        "[ChatAboutCandidatesService] Org chart generation complete. ChatId:",
        receivedChatId || this.currentChatId
      );
    } catch (error) {
      console.error("[ChatAboutCandidatesService] Org chart generation error:", error);
      throw error;
    }
  }
} 
